import type { ReactNode } from "react";
import { Link } from "react-router";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";

// Shared hero used by the four pillar landings (Save / Grow / Share / Match).
// Each page passes its own title, copy and image; the CTA is optional.
// Layout/spacing live in .smycles-hero-section (index.css) so Box Debug can
// pick it up and so we have a single source of truth for the layout.

// `anchor` lands as data-assistant-anchor on the rendered CTA so the Assistant's
// HighlightElement tool can point at it (see lib/assistant-ui-tools.ts).
type HeroCta =
  | { kind: "button"; label: string; onClick: () => void; anchor?: string }
  | { kind: "link"; label: string; to: string; anchor?: string };

interface HeroProps {
  title: string;
  subtitle: ReactNode;
  body: ReactNode;
  image: { src: string; alt?: string };
  cta?: HeroCta;
}

// Hero CTAs use the shared Button primitive so the look stays consistent with
// any other primary CTA in the app and future tweaks happen in one place.
const HERO_CTA_CLASS = buttonClasses({ variant: "primary", className: "mt-6" });

export function Hero({ title, subtitle, body, image, cta }: HeroProps) {
  return (
    <section className="smycles-hero-section">
      <div>
        <h1 className="font-serif text-6xl leading-[0.85] tracking-tight @sm:text-7xl @lg:text-[110px]">
          {title}
        </h1>
        <p className="mt-6 font-serif text-2xl leading-snug">{subtitle}</p>
        <p className="mt-4 max-w-md text-sm text-ink/70">{body}</p>
        {cta && <HeroCtaButton cta={cta} />}
      </div>
      <img src={image.src} alt={image.alt ?? ""} className="smycles-hero" />
    </section>
  );
}

function HeroCtaButton({ cta }: { cta: HeroCta }) {
  if (cta.kind === "link") {
    return (
      <Link to={cta.to} className={HERO_CTA_CLASS} data-assistant-anchor={cta.anchor}>
        {cta.label}
      </Link>
    );
  }
  return (
    <Button variant="primary" onClick={cta.onClick} className="mt-6" data-assistant-anchor={cta.anchor}>
      {cta.label}
    </Button>
  );
}
